/**
 * Block merger — resolves all BlockSlots into unified collections.
 * Resolves tokens, collects effects/observers/rules/layout from all blocks.
 *
 * Default flat grid: ALL blocks' `fields` nodes are merged into a single
 * continuous `FormFieldsNode`. Section wrappers are converted to inline
 * `section-divider` entries that render as full-width row breaks inside the
 * flat grid. Non-field nodes (udf, conditional, custom, etc.) are collected
 * and appended after the flat grid.
 *
 * The `group` property on BlockSlot is deprecated — flat merging is now
 * the default behavior for all blocks.
 */

import type { BlockSlot, BlockOverrides, FormEffect, FormObserver, FormRule } from '../types/block';
import type { FormLayoutNode, FormFieldsNode, FormSectionDividerEntry, FormSpacerEntry } from '../types/layout';
import type { FormFieldMapping } from '../types/fieldMapping';
import { resolveBlock } from '../resolver/tokenResolver';
import { applyOverridesToField } from './modeResolver';
import { evaluateCondition } from '../evaluators/conditionEvaluator';

export interface MergedBlocks {
  layout: FormLayoutNode[];
  effects: FormEffect[];
  observers: FormObserver[];
  rules: FormRule[];
  /** Aggregated block-level overrides from all slots (for runtime condition evaluation). */
  blockOverrides?: BlockOverrides;
}

type FlatFieldEntry = FormFieldMapping | FormSpacerEntry | FormSectionDividerEntry;

export function mergeBlocks(
  slots: BlockSlot[],
  paramsId?: string,
  formValues?: Record<string, unknown>,
): MergedBlocks {
  const effects: FormEffect[] = [];
  const observers: FormObserver[] = [];
  const rules: FormRule[] = [];
  let blockOverrides: BlockOverrides | undefined;
  let flatColumns: FormFieldsNode['columns'];

  const allLayouts: FormLayoutNode[][] = [];

  for (const rawSlot of slots) {
    const slot = resolveBlockSlot(rawSlot, paramsId, formValues);
    const resolved = resolveBlock(slot, paramsId);

    if (slot.overrides) {
      blockOverrides = mergeBlockOverrides(blockOverrides, slot.overrides);
    }
    if (resolved.effects) {
      effects.push(...filterEnabled(resolved.effects, slot.options));
    }
    if (resolved.observers) {
      observers.push(...filterEnabled(resolved.observers, slot.options));
    }
    if (resolved.rules) {
      rules.push(...filterEnabled(resolved.rules, slot.options));
    }

    applySlotOverridesToLayout(resolved.layout, slot.overrides);
    allLayouts.push(resolved.layout);

    if (!flatColumns) {
      flatColumns = findFirstFieldsColumns(resolved.layout);
    }
  }

  const hasInterleavedNonFieldNodes = allLayouts.some((nodes) =>
    hasNonFieldNodesBetweenFields(nodes),
  );

  let layout: FormLayoutNode[];

  if (hasInterleavedNonFieldNodes) {
    layout = buildOrderPreservingLayout(allLayouts, flatColumns);
  } else {
    layout = buildFlatMergedLayout(allLayouts, flatColumns);
  }

  return { layout, effects, observers, rules, blockOverrides };
}

function hasNonFieldNodesBetweenFields(nodes: FormLayoutNode[]): boolean {
  let seenFields = false;
  let seenNonFieldAfterFields = false;
  for (const node of nodes) {
    if (node.type === 'fields' || node.type === 'section' || node.type === 'stack' || node.type === 'surface' || node.type === 'grid') {
      if (seenNonFieldAfterFields) return true;
      seenFields = true;
    } else {
      if (seenFields) seenNonFieldAfterFields = true;
    }
  }
  return false;
}

function buildOrderPreservingLayout(
  allLayouts: FormLayoutNode[][],
  flatColumns: FormFieldsNode['columns'],
): FormLayoutNode[] {
  const result: FormLayoutNode[] = [];

  for (const nodes of allLayouts) {
    let pendingFields: FlatFieldEntry[] = [];
    let pendingUdfNodes: import('../types/layout').FormUdfNode[] = [];
    let currentCols: FormFieldsNode['columns'] = flatColumns;

    const flushPending = (): void => {
      if (pendingFields.length === 0 && pendingUdfNodes.length === 0) return;
      const mergedNode: FormFieldsNode = {
        type: 'fields',
        fields: pendingFields,
        columns: currentCols,
        ...(pendingUdfNodes.length > 0 && { inlineUdfNodes: pendingUdfNodes }),
      };
      result.push(mergedNode);
      pendingFields = [];
      pendingUdfNodes = [];
    };

    for (const node of nodes) {
      switch (node.type) {
        case 'fields':
          // When the incoming row has an explicit column count that differs from
          // the current pending batch, flush first so each distinct column layout
          // gets its own FormFieldsNode and its own grid container.
          if (node.columns != null && node.columns !== currentCols && pendingFields.length > 0) {
            flushPending();
          }
          if (node.columns != null) currentCols = node.columns;
          pendingFields.push(...node.fields);
          if (node.inlineUdfNodes) pendingUdfNodes.push(...node.inlineUdfNodes);
          break;

        case 'section': {
          const hasNonFlatChildren = node.children.some(
            (c) => c.type !== 'fields' && c.type !== 'section' && c.type !== 'stack' && c.type !== 'surface' && c.type !== 'grid',
          );
          if (hasNonFlatChildren) {
            flushPending();
            result.push(node);
          } else {
            if (node.title) {
              const divider: FormSectionDividerEntry = {
                type: 'section-divider',
                title: node.title,
                subtitle: node.subtitle,
                titleVariant: node.titleVariant,
                divider: node.divider,
                semanticId: node.semanticId,
              };
              pendingFields.push(divider);
            }
            extractFieldsFromLayoutFlat(node.children, pendingFields, pendingUdfNodes);
          }
          break;
        }

        case 'stack':
        case 'surface':
        case 'grid':
          extractFieldsFromLayoutFlat(node.children, pendingFields, pendingUdfNodes);
          break;

        default:
          flushPending();
          result.push(node);
          break;
      }
    }

    flushPending();
  }

  return result;
}

function extractFieldsFromLayoutFlat(
  nodes: FormLayoutNode[],
  flatFields: FlatFieldEntry[],
  inlineUdfNodes: import('../types/layout').FormUdfNode[],
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        flatFields.push(...node.fields);
        if (node.inlineUdfNodes) inlineUdfNodes.push(...node.inlineUdfNodes);
        break;
      case 'section':
        if (node.title) {
          flatFields.push({
            type: 'section-divider',
            title: node.title,
            subtitle: node.subtitle,
            titleVariant: node.titleVariant,
            divider: node.divider,
            semanticId: node.semanticId,
          });
        }
        extractFieldsFromLayoutFlat(node.children, flatFields, inlineUdfNodes);
        break;
      case 'stack':
      case 'surface':
      case 'grid':
        extractFieldsFromLayoutFlat(node.children, flatFields, inlineUdfNodes);
        break;
      default:
        break;
    }
  }
}

function buildFlatMergedLayout(
  allLayouts: FormLayoutNode[][],
  flatColumns: FormFieldsNode['columns'],
): FormLayoutNode[] {
  const flatFields: FlatFieldEntry[] = [];
  const trailingNodes: FormLayoutNode[] = [];
  const inlineUdfNodes: import('../types/layout').FormUdfNode[] = [];

  for (let i = 0; i < allLayouts.length; i++) {
    if (i > 0 && flatFields.length > 0) {
      flatFields.push({ type: 'section-divider', groupHint: true });
    }
    extractFieldsFromLayout(allLayouts[i], flatFields, trailingNodes, flatColumns, inlineUdfNodes);
  }

  const layout: FormLayoutNode[] = [];

  if (flatFields.length > 0) {
    const mergedNode: FormFieldsNode = {
      type: 'fields',
      fields: flatFields,
      columns: flatColumns,
      ...(inlineUdfNodes.length > 0 && { inlineUdfNodes }),
    };
    layout.push(mergedNode);
  }

  layout.push(...trailingNodes);

  return layout;
}

/**
 * Recursively extracts field entries from a layout tree into the flat list.
 * Section nodes are converted to section-divider entries.
 * Non-flattenable nodes (udf, conditional, custom, tabs, etc.) go to trailing.
 * inlineUdfNodes embedded in a fields node are collected into the provided array.
 *
 * IMPORTANT: When the layout interleaves `fields` and non-field nodes (e.g.,
 * `each`, `conditional`), we must preserve their relative order. If a non-field
 * node appears BETWEEN two `fields` nodes, we emit a "flush marker" so the
 * caller can split the flat grid at that point and interleave correctly.
 */
function extractFieldsFromLayout(
  nodes: FormLayoutNode[],
  flatFields: FlatFieldEntry[],
  trailingNodes: FormLayoutNode[],
  _flatColumns: FormFieldsNode['columns'],
  inlineUdfNodes?: import('../types/layout').FormUdfNode[],
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        flatFields.push(...node.fields);
        if (node.inlineUdfNodes && inlineUdfNodes) {
          inlineUdfNodes.push(...node.inlineUdfNodes);
        }
        break;

      case 'section': {
        if (node.title) {
          const divider: FormSectionDividerEntry = {
            type: 'section-divider',
            title: node.title,
            subtitle: node.subtitle,
            titleVariant: node.titleVariant,
            divider: node.divider,
            semanticId: node.semanticId,
          };
          flatFields.push(divider);
        }
        extractFieldsFromLayout(node.children, flatFields, trailingNodes, _flatColumns, inlineUdfNodes);
        break;
      }

      case 'stack':
      case 'surface':
        extractFieldsFromLayout(node.children, flatFields, trailingNodes, _flatColumns, inlineUdfNodes);
        break;

      case 'grid':
        extractFieldsFromLayout(node.children, flatFields, trailingNodes, _flatColumns, inlineUdfNodes);
        break;

      default:
        trailingNodes.push(node);
        break;
    }
  }
}

/** Walk down a layout tree to find the first `fields` node's `columns` spec. */
function findFirstFieldsColumns(nodes: FormLayoutNode[]): FormFieldsNode['columns'] | undefined {
  for (const node of nodes) {
    if (node.type === 'fields') return node.columns;
    if (node.type === 'section' || node.type === 'stack' || node.type === 'surface') {
      const found = findFirstFieldsColumns(node.children);
      if (found) return found;
    }
    if (node.type === 'grid') {
      const found = findFirstFieldsColumns(node.children);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Applies BlockSlot.overrides to field mappings in a layout subtree.
 * Static boolean overrides mutate the field; Condition overrides remain in
 * aggregated blockOverrides for runtime evaluation via fieldStateEvaluator.
 */
function applySlotOverridesToLayout(
  layout: FormLayoutNode[],
  overrides?: BlockOverrides,
): void {
  if (!overrides) return;

  walkLayoutFields(layout, (field) => {
    const suffix = field.name.split('.').pop() ?? field.name;
    const fieldOverride =
      overrides.fields?.[field.name] ?? overrides.fields?.[suffix];

    const scopedOverrides: BlockOverrides | undefined = fieldOverride
      ? { fields: { [suffix]: fieldOverride } }
      : undefined;

    const state = applyOverridesToField(
      suffix,
      field.required ?? false,
      undefined,
      scopedOverrides ?? pickBooleanBlockOverrides(overrides),
    );

    if (state.hidden) field.hidden = true;
    if (state.readOnly && typeof fieldOverride?.readOnly === 'boolean') {
      field.when = { ...field.when, readOnly: true };
    }
    if (state.disabled && typeof fieldOverride?.disabled === 'boolean') {
      field.when = { ...field.when, disabled: true };
    }
    if (state.required && typeof fieldOverride?.required === 'boolean') {
      field.required = true;
    }
    if (state.required === false && fieldOverride?.required === false) {
      field.required = false;
    }

    if (fieldOverride?.label) {
      field.overrideLabel = fieldOverride.label;
    }
    if (fieldOverride?.adapterHint) {
      field.overrideAdapterHint = fieldOverride.adapterHint;
    }

    if (typeof overrides.hidden === 'boolean' && overrides.hidden) {
      field.hidden = true;
    }
    if (typeof overrides.readOnly === 'boolean' && overrides.readOnly) {
      field.when = { ...field.when, readOnly: true };
    }
    if (typeof overrides.disabled === 'boolean' && overrides.disabled) {
      field.when = { ...field.when, disabled: true };
    }
  });
}

function pickBooleanBlockOverrides(overrides: BlockOverrides): BlockOverrides | undefined {
  const result: BlockOverrides = {};
  if (typeof overrides.readOnly === 'boolean') result.readOnly = overrides.readOnly;
  if (typeof overrides.hidden === 'boolean') result.hidden = overrides.hidden;
  if (typeof overrides.disabled === 'boolean') result.disabled = overrides.disabled;
  return Object.keys(result).length > 0 ? result : undefined;
}

function mergeBlockOverrides(
  base: BlockOverrides | undefined,
  next: BlockOverrides,
): BlockOverrides {
  return {
    readOnly: next.readOnly ?? base?.readOnly,
    hidden: next.hidden ?? base?.hidden,
    disabled: next.disabled ?? base?.disabled,
    fields: { ...base?.fields, ...next.fields },
  };
}

function walkLayoutFields(
  nodes: FormLayoutNode[],
  visit: (field: FormFieldMapping) => void,
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        for (const entry of node.fields) {
          if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'inline-action-button')) continue;
          visit(entry as FormFieldMapping);
        }
        break;
      case 'each':
        for (const field of node.itemFields) visit(field);
        break;
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        walkLayoutFields(node.children, visit);
        break;
      case 'tabs':
        for (const tab of node.tabs) walkLayoutFields(tab.children, visit);
        break;
      case 'conditional':
        walkLayoutFields(node.children, visit);
        if (node.else) walkLayoutFields(node.else, visit);
        break;
      case 'column-group':
        for (const group of node.groups) {
          for (const entry of group.fields) {
            if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'inline-action-button')) continue;
            visit(entry as FormFieldMapping);
          }
        }
        break;
      default:
        break;
    }
  }
}

function resolveBlockSlot(
  slot: BlockSlot,
  paramsId?: string,
  formValues?: Record<string, unknown>,
): BlockSlot {
  if (!slot.variants || !slot.activeVariant) return slot;

  let variantKey: string | undefined;
  if (typeof slot.activeVariant === 'string') {
    variantKey = slot.activeVariant;
  } else if (formValues) {
    const matches = evaluateCondition(slot.activeVariant, {
      fieldValue: undefined,
      values: formValues,
      meta: {},
      getOptionProp: () => undefined,
    });
    variantKey = matches ? Object.keys(slot.variants)[0] : Object.keys(slot.variants)[1];
  }

  const variantBlock = variantKey ? slot.variants[variantKey] : undefined;
  if (!variantBlock) return slot;

  return { ...slot, use: variantBlock };
}

function filterEnabled<T extends { enabled?: string }>(
  items: T[],
  options?: Record<string, unknown>,
): T[] {
  return items.filter((item) => {
    if (!item.enabled) return true;
    if (!options) return true;
    const value = options[item.enabled] ?? resolveBooleanString(item.enabled);
    return value === true || value === 'true';
  });
}

function resolveBooleanString(str: string): boolean {
  return str === 'true';
}
